import { useState, useEffect } from 'react';
import { Text } from '@qwickapps/react-framework';
import { config } from '../../config/runtime';

interface Goal {
  id: string;
  title: string;
  status: 'active' | 'blocked' | 'done' | 'cancelled';
  itemCount: number;
  completedCount: number;
}

interface GoalsSummaryProps {
  goals?: Goal[];
  onGoalClick?: (goalId: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--theme-color-primary)',
  done: '#4caf50',
  blocked: '#f44336',
  cancelled: '#9e9e9e',
};

export default function GoalsSummary({ goals: initialGoals, onGoalClick }: GoalsSummaryProps) {
  const [goals, setGoals] = useState<Goal[]>(initialGoals || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialGoals) {
      setGoals(initialGoals);
      return;
    }

    const fetchGoals = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${config.apiUrl}/api/projects/goals`);
        if (!response.ok) {
          throw new Error('Failed to fetch goals');
        }
        const data = await response.json();

        const mappedGoals: Goal[] = data.goals.map((g: any) => ({
          id: g.id,
          title: g.title,
          status: g.status,
          itemCount: 0,
          completedCount: 0,
        }));

        for (const goal of mappedGoals) {
          // Fetch all items (status=all) so we get accurate counts.
          // Use `total` from the response (not items.length) to avoid the
          // default limit=100 cap making itemCount appear as 100.
          const itemsResponse = await fetch(
            `${config.apiUrl}/api/projects/goals/${goal.id}/items?status=all&limit=1`,
          );
          if (itemsResponse.ok) {
            const itemsData = await itemsResponse.json();
            goal.itemCount = itemsData.total ?? itemsData.items?.length ?? 0;
          }
          // Fetch done items separately so completedCount is accurate even if
          // the goal has more items than the API page limit.
          const doneResponse = await fetch(
            `${config.apiUrl}/api/projects/goals/${goal.id}/items?status=done&limit=1`,
          );
          if (doneResponse.ok) {
            const doneData = await doneResponse.json();
            goal.completedCount = doneData.total ?? doneData.items?.length ?? 0;
          }
        }

        setGoals(mappedGoals);
      } catch (err) {
        console.error('Failed to fetch goals:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [initialGoals]);

  const activeGoals = goals.filter(g => g.status === 'active');
  const completedGoals = goals.filter(g => g.status === 'done');
  const blockedGoals = goals.filter(g => g.status === 'blocked');

  const totalItems = goals.reduce((sum, g) => sum + g.itemCount, 0);
  const completedItems = goals.reduce((sum, g) => sum + g.completedCount, 0);
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <div
      style={{
        padding: 24,
        borderRadius: 8,
        background: 'var(--theme-bg-surface)',
        border: '1px solid var(--theme-border-color)',
      }}
    >
      <Text variant="h6" content="Goals Overview" gutterBottom />
      {loading && <Text variant="body2" content="Loading..." customColor="var(--theme-text-secondary)" />}

      <div style={{ display: 'flex', gap: 32, marginTop: 16 }}>
        <StatBox label="Active" value={activeGoals.length} color="var(--theme-color-primary)" />
        <StatBox label="Completed" value={completedGoals.length} color="#4caf50" />
        <StatBox label="Blocked" value={blockedGoals.length} color="#f44336" />
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <Text variant="body2" content="Overall Progress" />
          <Text variant="body2" content={`${progressPercent}%`} customColor="var(--theme-color-primary)" />
        </div>
        <div
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          style={{
            height: 8,
            borderRadius: 4,
            background: 'var(--theme-border-color)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progressPercent}%`,
              background: 'var(--theme-color-primary)',
              borderRadius: 4,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        <Text
          variant="caption"
          content={`${completedItems} of ${totalItems} tasks completed`}
          customColor="var(--theme-text-secondary)"
        />
      </div>

      {/* Individual goal cards */}
      {goals.length > 0 && (
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {goals.map(goal => {
            const goalProgress = goal.itemCount > 0
              ? Math.round((goal.completedCount / goal.itemCount) * 100)
              : 0;
            const isClickable = !!onGoalClick;
            return (
              <div
                key={goal.id}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                data-testid={`goal-card-${goal.id}`}
                onClick={isClickable ? () => onGoalClick(goal.id) : undefined}
                onKeyDown={isClickable ? (e) => e.key === 'Enter' && onGoalClick(goal.id) : undefined}
                style={{
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--theme-border-color)',
                  background: 'var(--theme-bg-secondary)',
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: isClickable ? 'background 0.15s, border-color 0.15s' : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
                onMouseEnter={isClickable ? (e) => {
                  (e.currentTarget as HTMLDivElement).style.background = 'var(--theme-surface-hover, rgba(255,255,255,0.04))';
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--theme-color-primary)';
                } : undefined}
                onMouseLeave={isClickable ? (e) => {
                  (e.currentTarget as HTMLDivElement).style.background = 'var(--theme-bg-secondary)';
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--theme-border-color)';
                } : undefined}
              >
                {/* Status dot */}
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: STATUS_COLORS[goal.status] ?? '#9e9e9e',
                    flexShrink: 0,
                  }}
                />

                {/* Title + progress */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text variant="body2" content={goal.title} />
                  {goal.itemCount > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 4,
                          borderRadius: 2,
                          background: 'var(--theme-border-color)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${goalProgress}%`,
                            background: STATUS_COLORS[goal.status] ?? 'var(--theme-color-primary)',
                            borderRadius: 2,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                      <Text
                        variant="caption"
                        content={`${goal.completedCount}/${goal.itemCount}`}
                        customColor="var(--theme-text-secondary)"
                      />
                    </div>
                  )}
                </div>

                {/* Status badge */}
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: 10,
                    background: STATUS_COLORS[goal.status] ?? '#9e9e9e',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 600,
                    flexShrink: 0,
                    opacity: 0.85,
                  }}
                >
                  {goal.status}
                </span>

                {/* Arrow hint when clickable */}
                {isClickable && (
                  <span
                    style={{
                      color: 'var(--theme-text-secondary)',
                      fontSize: 12,
                      flexShrink: 0,
                    }}
                  >
                    ›
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface StatBoxProps {
  label: string;
  value: number;
  color: string;
}

function StatBox({ label, value, color }: StatBoxProps) {
  return (
    <div style={{ textAlign: 'center' }}>
      <Text
        variant="h4"
        content={String(value)}
        customColor={color}
      />
      <Text
        variant="caption"
        content={label}
        customColor="var(--theme-text-secondary)"
      />
    </div>
  );
}
